import React, { useMemo } from 'react';
import { Html, Float } from '@react-three/drei';
import { useSatelliteStore, getGeographicPosition, type SatelliteType as StoreSatelliteType } from '../../store/satelliteStore';
import {
  Vector3,
  Color
} from 'three';
import type { Satellite } from '../../store/satelliteStore';

export const HIGH_PERFORMANCE_THRESHOLD = 1000;

type SatelliteDisplayType = 'satellite' | 'debris' | 'iss' | 'starlink';

// Type mapping from store types to display types
const typeMapping: Record<StoreSatelliteType, SatelliteDisplayType> = {
  'active': 'satellite',
  'station': 'iss',
  'debris': 'debris',
  'starlink': 'starlink'
};

// Color constants
const COLORS = {
  satellite: new Color(0x00BFFF),  // Light blue
  debris: new Color(0xFF0000),     // Red
  iss: new Color(0x4F46E5),        // Purple
  starlink: new Color(0xFFD700)    // Yellow
};

// Scale constants
const SCALES: Record<SatelliteDisplayType, number> = {
  iss: 0.008,
  satellite: 0.015,
  debris: 0.02,
  starlink: 0.015
};

// Utility function to calculate satellite radius
const calculateRadius = (altitude: number): number => {
  // Convert altitude to relative scale
  // Earth radius is 6371 km
  // We want to make altitude differences more noticeable while keeping objects visible
  // Using a non-linear scale to make differences more apparent while keeping everything in view
  const altitudeScale = Math.log(1 + altitude / 100) / Math.log(1 + 1000);
  // Base radius is 1 (Earth's radius in our scale) plus scaled altitude
  return 1.05 + altitudeScale * 0.5;
};

const DetailedSatelliteModel: React.FC<{ 
  satellite: Satellite; 
  scale: number; 
  isSelected: boolean; 
  isHovered: boolean;
  disableAnimations: boolean;
}> = ({ satellite, scale, isSelected, isHovered, disableAnimations }) => {
  const displayType = typeMapping[satellite.type];
  const colorHex = COLORS[displayType].getStyle();
  const intensity = isSelected ? 1.5 : isHovered ? 0.8 : 0.5;

  const model = (
    <group scale={scale}>
      {displayType === 'iss' && (
        <>
          {/* Central Module */}
          <mesh>
            <cylinderGeometry args={[0.8, 0.8, 4, 8]} />
            <meshStandardMaterial
              color="#E8E8E8"
              metalness={0.8}
              roughness={0.2}
              emissive={colorHex}
              emissiveIntensity={intensity}
            />
          </mesh>
          
          {/* Cross Modules */}
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.6, 0.6, 6, 8]} />
            <meshStandardMaterial
              color="#D4D4D4"
              metalness={0.8}
              roughness={0.2}
              emissive={colorHex}
              emissiveIntensity={intensity}
            />
          </mesh>

          {/* Solar Panel Arrays */}
          {[-1, 1].map((side) => (
            <group key={side} position={[side * 3.5, 0, 0]}>
              <mesh rotation={[0, 0, Math.PI / 2]}>
                <boxGeometry args={[4, 0.1, 2]} />
                <meshStandardMaterial
                  color={colorHex}
                  metalness={0.6}
                  roughness={0.2}
                  emissive={colorHex}
                  emissiveIntensity={intensity * 0.8}
                />
              </mesh>
            </group>
          ))}

          {/* Additional Modules */}
          {[-1, 1].map((side) => (
            <group key={side} position={[0, 0, side * 1.5]}>
              <mesh>
                <cylinderGeometry args={[0.5, 0.5, 2, 8]} />
                <meshStandardMaterial
                  color="#C0C0C0"
                  metalness={0.8}
                  roughness={0.2}
                  emissive={colorHex}
                  emissiveIntensity={intensity}
                />
              </mesh>
            </group>
          ))}
        </>
      )}

      {displayType === 'satellite' && (
        <>
          {/* Main Body - Hexagonal */}
          <mesh>
            <cylinderGeometry args={[1, 1, 1.2, 6]} />
            <meshStandardMaterial
              color="#D4D4D4"
              metalness={0.9}
              roughness={0.1}
              emissive={colorHex}
              emissiveIntensity={intensity}
            />
          </mesh>
          
          {/* Main Antenna */}
          <mesh position={[0, 1, 0]}>
            <cylinderGeometry args={[0.05, 0.05, 1.5]} />
            <meshStandardMaterial
              color={colorHex}
              metalness={0.8}
              roughness={0.2}
              emissive={colorHex}
              emissiveIntensity={intensity}
            />
          </mesh>

          {/* Solar Panels */}
          {[-1, 1].map((side) => (
            <mesh key={side} position={[side * 1.5, 0, 0]} rotation={[0, 0, Math.PI / 6 * side]}>
              <boxGeometry args={[1.2, 0.05, 0.8]} />
              <meshStandardMaterial
                color={colorHex}
                metalness={0.7}
                roughness={0.2}
                emissive={colorHex}
                emissiveIntensity={intensity}
              />
            </mesh>
          ))}
        </>
      )}

      {displayType === 'starlink' && (
        <>
          {/* Flat Main Body */}
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <boxGeometry args={[2, 0.2, 1.5]} />
            <meshStandardMaterial
              color="#D4D4D4"
              metalness={0.9}
              roughness={0.1}
              emissive={colorHex}
              emissiveIntensity={intensity}
            />
          </mesh>

          {/* Communication Array */}
          <mesh position={[0, 0.2, 0]}>
            <boxGeometry args={[0.3, 0.1, 0.3]} />
            <meshStandardMaterial
              color={colorHex}
              metalness={0.8}
              roughness={0.2}
              emissive={colorHex}
              emissiveIntensity={intensity}
            />
          </mesh>

          {/* Solar Panel (Single Large Panel) */}
          <mesh position={[0, -0.2, 0]} rotation={[0, 0, 0]}>
            <boxGeometry args={[2.2, 0.02, 1.7]} />
            <meshStandardMaterial
              color={colorHex}
              metalness={0.7}
              roughness={0.2}
              emissive={colorHex}
              emissiveIntensity={intensity}
            />
          </mesh>
        </>
      )}

      {displayType === 'debris' && (
        <>
          {/* Irregular Debris Shape */}
          <group rotation={[Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI]}>
            <mesh>
              <octahedronGeometry args={[0.5]} />
              <meshStandardMaterial
                color="#8B8B8B"
                metalness={0.8}
                roughness={0.5}
                emissive={colorHex}
                emissiveIntensity={intensity}
              />
            </mesh>
            <mesh position={[0.3, 0.2, 0]}>
              <tetrahedronGeometry args={[0.3]} />
              <meshStandardMaterial
                color="#8B8B8B"
                metalness={0.8}
                roughness={0.5}
                emissive={colorHex}
                emissiveIntensity={intensity}
              />
            </mesh>
          </group>
        </>
      )}

      {isSelected && (
        <mesh scale={3}>
          <sphereGeometry args={[1, 16, 16]} />
          <meshPhongMaterial
            color={colorHex}
            transparent={true}
            opacity={0.2}
            depthWrite={false}
            side={1}
            blending={2}
          />
        </mesh>
      )}
    </group>
  );

  if (disableAnimations) {
    return model;
  }

  return (
    <Float
      speed={1.5}
      rotationIntensity={0.2}
      floatIntensity={0.5}
    >
      {model}
    </Float>
  );
};

const SatelliteTooltip: React.FC<{ satellite: Satellite }> = ({ satellite }) => (
  <Html
    center
    style={{
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: '4px 8px',
      borderRadius: '4px',
      color: COLORS[typeMapping[satellite.type]].getStyle(),
      fontSize: '12px',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
      transform: 'scale(0.75)',
      zIndex: 1000
    }}
    distanceFactor={8}
    zIndexRange={[1000, 0]}
  >
    <div>
      <div style={{ fontWeight: 'bold' }}>{satellite.name}</div>
      <div>Alt: {getGeographicPosition(satellite.position).alt.toFixed(0)} km</div>
    </div>
  </Html>
);

const calculatePosition = (satellite: Satellite, time: Date, disableAnimations: boolean): Vector3 => {
  const geoPosition = getGeographicPosition(satellite.position);
  
  // If animations are disabled, use a fixed time step for updates
  if (disableAnimations) {
    // Round time to nearest 5 seconds to reduce updates
    time.setMilliseconds(0);
    time.setSeconds(Math.floor(time.getSeconds() / 5) * 5);
  }

  const phi = (90 - geoPosition.lat) * (Math.PI / 180);
  const theta = (geoPosition.lng + 180) * (Math.PI / 180);
  const radius = calculateRadius(geoPosition.alt);

  return new Vector3(
    radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
};

const SatelliteMarker: React.FC = () => {
  const satellites = useSatelliteStore(state => state.satellites);
  const selectedSatellite = useSatelliteStore(state => state.selectedSatellite);
  const hoveredSatellite = useSatelliteStore(state => state.hoveredSatellite);
  const setHoveredSatellite = useSatelliteStore(state => state.setHoveredSatellite);
  const objectLimit = useSatelliteStore(state => state.objectLimit);

  const handleHover = (satellite: Satellite | null) => {
    setHoveredSatellite(satellite ? satellite.id : null);
  };

  const categorizedSatellites = useMemo(() => {
    const categories: Record<SatelliteDisplayType, Satellite[]> = {
      satellite: [],
      debris: [],
      iss: [],
      starlink: []
    };

    // First, categorize all satellites
    satellites.forEach(sat => {
      const type = typeMapping[sat.type];
      if (type) {
        categories[type].push(sat);
      }
    });

    // Count active categories
    const activeCategories = Object.entries(categories)
      .filter(([_, sats]) => sats.length > 0)
      .map(([type]) => type as SatelliteDisplayType);

    if (activeCategories.length === 0) return categories;

    // Calculate satellites per category
    const perCategory = Math.floor(objectLimit / activeCategories.length);
    
    // Ensure at least one satellite per active category
    const minPerCategory = Math.max(1, Math.min(perCategory, 
      Math.min(...activeCategories.map(cat => categories[cat].length))
    ));

    // Distribute remaining slots evenly
    let remaining = objectLimit - (minPerCategory * activeCategories.length);
    const extraPerCategory = Math.floor(remaining / activeCategories.length);

    // Create new categories with limited satellites
    const limitedCategories: Record<SatelliteDisplayType, Satellite[]> = {
      satellite: [],
      debris: [],
      iss: [],
      starlink: []
    };

    activeCategories.forEach(category => {
      const categoryLimit = minPerCategory + extraPerCategory;
      const availableSats = categories[category];
      const selectedSats = availableSats
        .slice(0, Math.min(categoryLimit, availableSats.length));
      limitedCategories[category] = selectedSats;
    });

    return limitedCategories;
  }, [satellites, objectLimit]);

  // Determine if we should disable animations based on total visible satellites
  const totalVisibleSatellites = useMemo(() => {
    return Object.values(categorizedSatellites).reduce((acc, sats) => acc + sats.length, 0);
  }, [categorizedSatellites]);

  const disableAnimations = totalVisibleSatellites > HIGH_PERFORMANCE_THRESHOLD;

  return (
    <>
      {Object.entries(categorizedSatellites).map(([type, sats]) => (
        sats.length > 0 && (
          <group key={type}>
            {sats.map((sat) => (
              <group 
                key={sat.id} 
                position={calculatePosition(sat, new Date(), disableAnimations)}
                onPointerOver={(e) => {
                  e.stopPropagation();
                  handleHover(sat);
                }}
                onPointerOut={(e) => {
                  e.stopPropagation();
                  handleHover(null);
                }}
              >
                <DetailedSatelliteModel
                  satellite={sat}
                  scale={SCALES[type as SatelliteDisplayType]}
                  isSelected={selectedSatellite === sat.id}
                  isHovered={hoveredSatellite === sat.id}
                  disableAnimations={disableAnimations}
                />
                {hoveredSatellite === sat.id && (
                  <SatelliteTooltip satellite={sat} />
                )}
              </group>
            ))}
          </group>
        )
      ))}
    </>
  );
};

export default SatelliteMarker; 